import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useBranding } from '@/hooks/useBranding';
import RestaurantLogo from '@/components/branding/RestaurantLogo';
import JustEatButton from '@/components/feature/JustEatButton';
import { FOOTER_COPY, SITE } from '@/content/yawasCopy';
import { LEGAL_FOOTER_LINKS } from '@/content/legalPages';
import { DEFAULT_RESTAURANT_HOURS, parseOpeningHours } from '@/content/openingHours';
import OpeningHoursList from '@/components/feature/OpeningHoursList';

const Footer = () => {
  const { get } = useSiteSettings();
  const { restaurantName, logoSize } = useBranding();
  const footerLogoHeight = Math.max(96, Math.min(Math.round(logoSize * 1.35), 160));

  const name = get('restaurant_name', restaurantName);
  const footerDesc = get('footer_description', '') || FOOTER_COPY.description;
  const cateringText = get('footer_catering_text', '') || FOOTER_COPY.catering;
  const phone = get('restaurant_phone', SITE.phoneDisplay) || SITE.phoneDisplay;
  const email = get('restaurant_email', SITE.email) || SITE.email;
  const openingHours = parseOpeningHours(get('restaurant_hours', DEFAULT_RESTAURANT_HOURS));
  const instagram = get('social_instagram', '#');
  const facebook = get('social_facebook', '#');

  return (
    <footer style={{ backgroundColor: '#1a1a1a' }} className="text-stone-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
          <div>
            <div className="mb-5">
              <RestaurantLogo maxHeight={footerLogoHeight} invert />
            </div>
            <p className="text-sm text-stone-400 leading-relaxed mb-5">{footerDesc}</p>
            <div className="flex items-center gap-3">
              <a href={instagram} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm text-stone-300"></i>
              </a>
              <a href={facebook} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 transition-colors cursor-pointer">
                <i className="ri-facebook-line text-sm text-stone-300"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Öffnungszeiten</h4>
            <div className="mb-6">
              <OpeningHoursList rows={openingHours} variant="dark" />
            </div>
            <h4 className="text-white font-semibold text-sm mb-3 uppercase tracking-wider">Navigation</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-stone-400 hover:text-amber-400 transition-colors">
                  Startseite
                </Link>
              </li>
              <li>
                <Link to="/menu" className="text-sm text-stone-400 hover:text-amber-400 transition-colors">
                  Speisekarte
                </Link>
              </li>
              <li>
                <JustEatButton className="text-sm text-stone-400 hover:text-amber-400 transition-colors">
                  Auf Just Eat bestellen
                </JustEatButton>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Kontakt</h4>
            <ul className="space-y-4">
              {SITE.addressOneLine && (
                <li className="flex items-start gap-3 text-sm text-stone-400">
                  <i className="ri-map-pin-line text-amber-400 mt-0.5"></i>
                  <span className="whitespace-pre-line">{SITE.addressLines.join('\n')}</span>
                </li>
              )}
              {phone && (
                <li className="flex items-center gap-3 text-sm text-stone-400">
                  <i className="ri-phone-line text-amber-400"></i>
                  <a href={`tel:${SITE.phoneTel}`} className="hover:text-amber-400 transition-colors">{phone}</a>
                </li>
              )}
              {email && (
                <li className="flex items-center gap-3 text-sm text-stone-400">
                  <i className="ri-mail-line text-amber-400"></i>
                  <a href={`mailto:${email}`} className="hover:text-amber-400 transition-colors">{email}</a>
                </li>
              )}
            </ul>
            {phone && (
              <div className="mt-6 p-4 rounded-xl bg-stone-800 border border-stone-700">
                <p className="text-xs text-stone-400 mb-2">{cateringText}</p>
                <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-2 text-amber-400 text-sm font-semibold hover:text-amber-300 transition-colors">
                  <i className="ri-phone-line"></i>
                  Jetzt anrufen
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-stone-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-xs text-stone-500">
            <p>&copy; {new Date().getFullYear()} {name}. Alle Rechte vorbehalten.</p>
            <span className="hidden sm:inline text-stone-700" aria-hidden>
              |
            </span>
            <p>
              Website von{' '}
              <a
                href="https://kreativweb.ch/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-400 hover:text-amber-400 transition-colors underline-offset-2 hover:underline"
              >
                Kreativweb
              </a>
            </p>
          </div>
          <div className="flex items-center gap-5">
            {LEGAL_FOOTER_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-xs text-stone-500 hover:text-stone-300 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
